import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  validateTargetUrl,
  DEFAULT_ROE,
  type ProjectType,
  type RepoConfig,
  type EndpointConfig,
  type RepoProvider,
  type RepoAccessMethod,
  type EndpointAuthKind,
  type EndpointSpecKind,
} from '@nirvik/shared';
import { apiClient } from '@/lib/api-client';
import { Globe, GitBranch, Radar, Loader2 } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { cn } from '@/lib/utils';

// ───────────────────────────────────────────────────────────────────────────

type FormErrors = Record<string, string>;

export function NewProjectPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { orgId, portfolioId } = useParams<{ orgId: string; portfolioId: string }>();

  const [name, setName] = useState('');
  const [targetUrl, setTargetUrl] = useState('');
  const [description, setDescription] = useState('');
  const [projectType, setProjectType] = useState<ProjectType>('repo');
  const [errors, setErrors] = useState<FormErrors>({});

  // Repo config
  const [repoProvider, setRepoProvider] = useState<RepoProvider>('github');
  const [gitUrl, setGitUrl] = useState('');
  const [branch, setBranch] = useState('main');
  const [repoAccess, setRepoAccess] = useState<RepoAccessMethod>('public');
  // GitHub App picker state — only used when repoAccess === 'github-app'.
  const [installationId, setInstallationId] = useState<string>('');
  const [ghOwner, setGhOwner] = useState<string>('');
  const [ghRepoName, setGhRepoName] = useState<string>('');
  const [ghRepoId, setGhRepoId] = useState<number | null>(null);

  // Endpoint config
  const [baseUrl, setBaseUrl] = useState('');
  const [hostnames, setHostnames] = useState(''); // comma-separated in form
  const [authKind, setAuthKind] = useState<EndpointAuthKind>('none');
  const [authHeaderName, setAuthHeaderName] = useState('');
  const [specKind, setSpecKind] = useState<EndpointSpecKind>('none');
  const [specUrl, setSpecUrl] = useState('');
  const [allowDestructive, setAllowDestructive] = useState(false);
  const [rateLimitRps, setRateLimitRps] = useState(DEFAULT_ROE.rateLimitRps);

  const mutation = useMutation({
    mutationFn: () => {
      let repoConfig: RepoConfig | undefined;
      let endpointConfig: EndpointConfig | undefined;

      if (projectType === 'repo' || projectType === 'hybrid') {
        if (repoAccess === 'github-app') {
          const fullName = ghOwner && ghRepoName ? `${ghOwner}/${ghRepoName}` : '';
          repoConfig = {
            provider: 'github',
            gitUrl: fullName ? `https://github.com/${fullName}.git` : gitUrl.trim(),
            branch: branch.trim() || 'main',
            accessMethod: 'github-app',
            installationId: installationId || undefined,
            ownerLogin: ghOwner || undefined,
            repoName: ghRepoName || undefined,
            repoId: ghRepoId ?? undefined,
          };
        } else {
          repoConfig = {
            provider: repoProvider,
            gitUrl: gitUrl.trim(),
            branch: branch.trim() || 'main',
            accessMethod: repoAccess,
          };
        }
      }
      if (projectType === 'endpoint' || projectType === 'hybrid') {
        endpointConfig = {
          baseUrl: baseUrl.trim(),
          hostnames: hostnames
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean),
          auth: {
            kind: authKind,
            ...(authHeaderName ? { headerName: authHeaderName.trim() } : {}),
          },
          spec: {
            kind: specKind,
            ...(specUrl ? { url: specUrl.trim() } : {}),
          },
          rulesOfEngagement: {
            allowDestructive,
            rateLimitRps,
          },
        };
      }

      return apiClient.createProject(orgId!, portfolioId!, {
        name: name.trim(),
        // Server derives targetUrl from repoConfig.gitUrl / endpointConfig.baseUrl
        // when not provided, so repo projects don't need a separate Display URL.
        targetUrl: targetUrl.trim() || undefined,
        description: description.trim() || undefined,
        projectType,
        repoConfig,
        endpointConfig,
      });
    },
    onSuccess: () => {
      // Invalidate caches so the portfolio detail page (project list +
      // dashboard widgets) and the org dashboard pick up the new
      // project on navigation. Without this, React Query serves the
      // stale list from cache until the background refetch completes.
      queryClient.invalidateQueries({ queryKey: ['org', orgId, 'portfolio', portfolioId, 'projects'] });
      queryClient.invalidateQueries({ queryKey: ['portfolio', orgId, portfolioId, 'dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['org', orgId, 'dashboard'] });
      navigate(`/orgs/${orgId}/portfolios/${portfolioId}`);
    },
    onError: (err: Error) => setErrors({ submit: err.message }),
  });

  const isSubmitting = mutation.isPending;

  const validate = () => {
    const newErrors: FormErrors = {};
    if (!name.trim()) newErrors.name = 'Give your project a name';
    if (name.length > 100) newErrors.name = 'Name is too long (max 100 characters)';

    // Display URL is optional — the server will derive one from the repo or
    // endpoint config if blank. Only validate shape when the user typed one.
    if (targetUrl.trim()) {
      const result = validateTargetUrl(targetUrl);
      if (!result.valid) newErrors.targetUrl = result.error!;
    }

    if (projectType === 'repo' || projectType === 'hybrid') {
      if (repoAccess === 'github-app') {
        if (!installationId) newErrors.gitUrl = 'Pick a GitHub installation';
        else if (!ghOwner || !ghRepoName) newErrors.gitUrl = 'Pick a repository';
        if (!branch.trim()) newErrors.branch = 'Pick a branch';
      } else {
        if (!gitUrl.trim()) newErrors.gitUrl = 'Git URL is required for repo projects';
        if (!branch.trim()) newErrors.branch = 'Branch is required';
      }
    }

    if (projectType === 'endpoint' || projectType === 'hybrid') {
      if (!baseUrl.trim()) {
        newErrors.baseUrl = 'Base URL is required for endpoint projects';
      } else {
        const result = validateTargetUrl(baseUrl);
        if (!result.valid) newErrors.baseUrl = result.error!;
      }
      if (specKind !== 'none' && !specUrl.trim()) {
        newErrors.specUrl = 'Provide a URL to the spec (or leave spec kind as None)';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    mutation.mutate();
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-foreground">Add a New Project</h1>
        <p className="text-muted-foreground dark:text-muted-foreground mt-2">
          Tell us what you want to analyze — a source code repository, a live endpoint we can
          red-team, or both.
        </p>
      </div>

      <Card className="p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Project type picker */}
          <div className="space-y-2">
            <Label>What are we analyzing?</Label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <ProjectTypeTile
                active={projectType === 'repo'}
                onClick={() => setProjectType('repo')}
                icon={<GitBranch className="h-4 w-4" />}
                title="Source repo"
                subtitle="Scan the codebase with blue-team agents"
              />
              <ProjectTypeTile
                active={projectType === 'endpoint'}
                onClick={() => setProjectType('endpoint')}
                icon={<Radar className="h-4 w-4" />}
                title="Live endpoint"
                subtitle="Red-team a running service"
              />
              <ProjectTypeTile
                active={projectType === 'hybrid'}
                onClick={() => setProjectType('hybrid')}
                icon={<Globe className="h-4 w-4" />}
                title="Both"
                subtitle="Repo + endpoint together"
              />
            </div>
          </div>

          {/* Project Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Project Name</Label>
            <Input
              id="name"
              placeholder="e.g., My Portfolio Site"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (errors.name) setErrors({ ...errors, name: '' });
              }}
            />
            {errors.name && <p className="text-sm text-red-600">{errors.name}</p>}
          </div>

          {/* Display URL — optional, only useful when you want something
              different from the repo's git URL or the endpoint's base URL. */}
          {projectType !== 'repo' && (
            <div className="space-y-2">
              <Label htmlFor="targetUrl">
                Display URL <span className="text-muted-foreground">(optional)</span>
              </Label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="targetUrl"
                  className="pl-10"
                  placeholder="e.g., example.com"
                  value={targetUrl}
                  onChange={(e) => {
                    setTargetUrl(e.target.value);
                    if (errors.targetUrl) setErrors({ ...errors, targetUrl: '' });
                  }}
                />
              </div>
              {errors.targetUrl && <p className="text-sm text-red-600">{errors.targetUrl}</p>}
              <p className="text-xs text-muted-foreground dark:text-muted-foreground">
                Shown in the project list and reports. Leave blank to use the endpoint's base URL.
              </p>
            </div>
          )}

          {/* Repo section */}
          {(projectType === 'repo' || projectType === 'hybrid') && (
            <div className="space-y-4 border-t border-gray-200 dark:border-border pt-5">
              <div className="flex items-center gap-2">
                <GitBranch className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-sm font-semibold text-gray-900 dark:text-foreground">
                  Repository
                </h3>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="repoProvider">Provider</Label>
                  <select
                    id="repoProvider"
                    value={repoProvider}
                    onChange={(e) => setRepoProvider(e.target.value as RepoProvider)}
                    className="w-full rounded-md border border-gray-300 dark:border-border bg-white dark:bg-card text-sm px-3 py-2"
                  >
                    <option value="github">GitHub</option>
                    <option value="gitlab">GitLab</option>
                    <option value="bitbucket">Bitbucket</option>
                    <option value="generic-git">Generic Git</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="repoAccess">Access</Label>
                  <select
                    id="repoAccess"
                    value={repoAccess}
                    onChange={(e) => setRepoAccess(e.target.value as RepoAccessMethod)}
                    className="w-full rounded-md border border-gray-300 dark:border-border bg-white dark:bg-card text-sm px-3 py-2"
                  >
                    <option value="public">Public</option>
                    <option value="github-app">GitHub App</option>
                    <option value="deploy-key">Deploy key</option>
                    <option value="pat">Personal access token</option>
                  </select>
                </div>
              </div>

              {repoAccess === 'github-app' ? (
                <>
                  <GithubRepoPicker
                    orgId={orgId!}
                    installationId={installationId}
                    setInstallationId={setInstallationId}
                    ghOwner={ghOwner}
                    ghRepoName={ghRepoName}
                    setRepo={(owner, name, id) => { setGhOwner(owner); setGhRepoName(name); setGhRepoId(id); }}
                    branch={branch}
                    setBranch={setBranch}
                  />
                  {errors.gitUrl && <p className="text-sm text-red-600">{errors.gitUrl}</p>}
                  {errors.branch && <p className="text-sm text-red-600">{errors.branch}</p>}
                </>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="gitUrl">Git URL</Label>
                    <Input
                      id="gitUrl"
                      placeholder="https://github.com/org/repo.git"
                      value={gitUrl}
                      onChange={(e) => setGitUrl(e.target.value)}
                    />
                    {errors.gitUrl && <p className="text-sm text-red-600">{errors.gitUrl}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="branch">Branch</Label>
                    <Input id="branch" value={branch} onChange={(e) => setBranch(e.target.value)} />
                    {errors.branch && <p className="text-sm text-red-600">{errors.branch}</p>}
                  </div>
                  {repoAccess !== 'public' && (
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      Credentials will be configured after the project is created, from Project
                      Settings.
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {/* Endpoint section */}
          {(projectType === 'endpoint' || projectType === 'hybrid') && (
            <div className="space-y-4 border-t border-gray-200 dark:border-border pt-5">
              <div className="flex items-center gap-2">
                <Radar className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-sm font-semibold text-gray-900 dark:text-foreground">
                  Endpoint (red-team target)
                </h3>
              </div>

              <div className="space-y-2">
                <Label htmlFor="baseUrl">Base URL</Label>
                <Input
                  id="baseUrl"
                  placeholder="https://api.example.com"
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                />
                {errors.baseUrl && <p className="text-sm text-red-600">{errors.baseUrl}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="hostnames">Additional in-scope hostnames</Label>
                <Input
                  id="hostnames"
                  placeholder="api-staging.example.com, cdn.example.com"
                  value={hostnames}
                  onChange={(e) => setHostnames(e.target.value)}
                />
                <p className="text-xs text-muted-foreground dark:text-muted-foreground">
                  Comma-separated. Only hostnames in this list will be probed.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="authKind">Auth</Label>
                  <select
                    id="authKind"
                    value={authKind}
                    onChange={(e) => setAuthKind(e.target.value as EndpointAuthKind)}
                    className="w-full rounded-md border border-gray-300 dark:border-border bg-white dark:bg-card text-sm px-3 py-2"
                  >
                    <option value="none">None</option>
                    <option value="api-key">API key</option>
                    <option value="bearer">Bearer token</option>
                    <option value="basic">Basic auth</option>
                    <option value="oauth2">OAuth 2.0</option>
                    <option value="cookie">Session cookie</option>
                  </select>
                </div>
                {(authKind === 'api-key' || authKind === 'cookie') && (
                  <div className="space-y-2">
                    <Label htmlFor="authHeaderName">
                      {authKind === 'cookie' ? 'Cookie name' : 'Header name'}
                    </Label>
                    <Input
                      id="authHeaderName"
                      placeholder={authKind === 'cookie' ? 'session' : 'X-API-Key'}
                      value={authHeaderName}
                      onChange={(e) => setAuthHeaderName(e.target.value)}
                    />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="specKind">API spec</Label>
                  <select
                    id="specKind"
                    value={specKind}
                    onChange={(e) => setSpecKind(e.target.value as EndpointSpecKind)}
                    className="w-full rounded-md border border-gray-300 dark:border-border bg-white dark:bg-card text-sm px-3 py-2"
                  >
                    <option value="none">None</option>
                    <option value="openapi">OpenAPI</option>
                    <option value="graphql">GraphQL SDL</option>
                    <option value="postman">Postman collection</option>
                  </select>
                </div>
                {specKind !== 'none' && (
                  <div className="space-y-2">
                    <Label htmlFor="specUrl">Spec URL</Label>
                    <Input
                      id="specUrl"
                      placeholder="https://api.example.com/openapi.json"
                      value={specUrl}
                      onChange={(e) => setSpecUrl(e.target.value)}
                    />
                    {errors.specUrl && <p className="text-sm text-red-600">{errors.specUrl}</p>}
                  </div>
                )}
              </div>
              {specKind !== 'none' && (
                <p className="text-xs text-muted-foreground dark:text-muted-foreground">
                  Or upload a spec file from Project Settings after the project is created.
                </p>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="flex items-center gap-2">
                  <input
                    id="allowDestructive"
                    type="checkbox"
                    checked={allowDestructive}
                    onChange={(e) => setAllowDestructive(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <Label htmlFor="allowDestructive" className="text-sm">
                    Allow potentially destructive tests
                  </Label>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="rateLimit">Rate limit (req/s)</Label>
                  <Input
                    id="rateLimit"
                    type="number"
                    min={1}
                    max={100}
                    value={rateLimitRps}
                    onChange={(e) => setRateLimitRps(parseInt(e.target.value, 10) || 1)}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">
              Description <span className="text-muted-foreground">(optional)</span>
            </Label>
            <Textarea
              id="description"
              placeholder="What does this project do? Any specific areas of concern?"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>

          {errors.submit && (
            <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-500 rounded-md p-3">
              {errors.submit}
            </p>
          )}

          <div className="flex gap-3 pt-2">
            <Button type="submit" disabled={isSubmitting} className="flex-1">
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Project'
              )}
            </Button>
            <Link to={`/orgs/${orgId}/portfolios/${portfolioId}`}>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </Link>
          </div>
        </form>
      </Card>
    </div>
  );
}

function ProjectTypeTile({
  active,
  onClick,
  icon,
  title,
  subtitle,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex flex-col items-start gap-1 rounded-lg border p-3 text-left transition-colors',
        active
          ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-500/10 ring-2 ring-emerald-500'
          : 'border-gray-200 dark:border-border hover:bg-gray-50 dark:hover:bg-muted/50',
      )}
    >
      <div className="flex items-center gap-2 text-gray-900 dark:text-foreground">
        {icon}
        <span className="text-sm font-semibold">{title}</span>
      </div>
      <p className="text-xs text-muted-foreground dark:text-muted-foreground">{subtitle}</p>
    </button>
  );
}

// ─── GitHub repo picker ────────────────────────────────────────────────────
// Three cascading dropdowns: installation → repo → branch. Driven by
// /v1/orgs/:orgId/github/installations and friends. Defaults the branch
// to the repo's default_branch the first time a repo is selected.

interface GithubRepoPickerProps {
  orgId: string;
  installationId: string;
  setInstallationId: (id: string) => void;
  ghOwner: string;
  ghRepoName: string;
  setRepo: (owner: string, name: string, id: number | null) => void;
  branch: string;
  setBranch: (b: string) => void;
}

function GithubRepoPicker({
  orgId, installationId, setInstallationId,
  ghOwner, ghRepoName, setRepo, branch, setBranch,
}: GithubRepoPickerProps) {
  type Installation = { installationId: string; accountLogin: string; accountType: string };
  type Repo = { id: number; name: string; fullName: string; defaultBranch: string; private: boolean };
  type Branch = { name: string; protected: boolean };

  const installations = useQuery({
    queryKey: ['github', 'installations', orgId],
    queryFn: () => apiClient.listGithubInstallations(orgId),
  });
  const repos = useQuery({
    queryKey: ['github', 'repos', orgId, installationId],
    queryFn: () => apiClient.listGithubRepos(orgId, installationId),
    enabled: !!installationId,
  });
  const branches = useQuery({
    queryKey: ['github', 'branches', orgId, installationId, ghOwner, ghRepoName],
    queryFn: () => apiClient.listGithubBranches(orgId, installationId, ghOwner, ghRepoName),
    enabled: !!installationId && !!ghOwner && !!ghRepoName,
  });

  const installRows = ((installations.data?.data as Installation[] | undefined) ?? []);
  const repoRows = ((repos.data?.data as Repo[] | undefined) ?? []);
  const branchRows = ((branches.data?.data as Branch[] | undefined) ?? []);

  if (installations.isLoading) {
    return <p className="text-xs text-muted-foreground">Loading GitHub installations…</p>;
  }

  if (installRows.length === 0) {
    return (
      <div className="rounded-md border border-amber-300 dark:border-amber-700/50 bg-amber-50 dark:bg-amber-900/15 p-3 text-xs text-amber-900 dark:text-amber-300">
        No GitHub installations linked to this org yet.{' '}
        <Link to={`/orgs/${orgId}/settings?tab=integrations`} className="underline font-medium">
          Open Org settings → Integrations
        </Link>{' '}
        and install the Trinetra GitHub App, then come back to pick a repo.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <Label htmlFor="ghInstall">GitHub installation</Label>
        <select
          id="ghInstall"
          value={installationId}
          onChange={(e) => {
            setInstallationId(e.target.value);
            setRepo('', '', null);
          }}
          className="w-full rounded-md border border-gray-300 dark:border-border bg-white dark:bg-card text-sm px-3 py-2"
        >
          <option value="">— pick an installation —</option>
          {installRows.map((i) => (
            <option key={i.installationId} value={i.installationId}>
              {i.accountLogin} ({i.accountType})
            </option>
          ))}
        </select>
      </div>

      {installationId && (
        <div className="space-y-2">
          <Label htmlFor="ghRepo">Repository</Label>
          {repos.isLoading ? (
            <p className="text-xs text-muted-foreground">Loading repos…</p>
          ) : (
            <select
              id="ghRepo"
              value={ghOwner && ghRepoName ? `${ghOwner}/${ghRepoName}` : ''}
              onChange={(e) => {
                const fullName = e.target.value;
                if (!fullName) { setRepo('', '', null); return; }
                const repo = repoRows.find((r) => r.fullName === fullName);
                if (!repo) return;
                const [owner, name] = fullName.split('/');
                setRepo(owner, name, repo.id);
                if (!branch || branch === 'main') setBranch(repo.defaultBranch || 'main');
              }}
              className="w-full rounded-md border border-gray-300 dark:border-border bg-white dark:bg-card text-sm px-3 py-2"
            >
              <option value="">— pick a repo —</option>
              {repoRows.map((r) => (
                <option key={r.id} value={r.fullName}>
                  {r.fullName}{r.private ? ' (private)' : ''}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      {ghOwner && ghRepoName && (
        <div className="space-y-2">
          <Label htmlFor="ghBranch">Branch</Label>
          {branches.isLoading ? (
            <p className="text-xs text-muted-foreground">Loading branches…</p>
          ) : (
            <select
              id="ghBranch"
              value={branch}
              onChange={(e) => setBranch(e.target.value)}
              className="w-full rounded-md border border-gray-300 dark:border-border bg-white dark:bg-card text-sm px-3 py-2"
            >
              {branchRows.map((b) => (
                <option key={b.name} value={b.name}>
                  {b.name}{b.protected ? ' 🔒' : ''}
                </option>
              ))}
            </select>
          )}
        </div>
      )}
    </div>
  );
}
